import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Calendar, ChevronLeft, ChevronRight, Loader2, User, Mail, Phone, 
  CreditCard, CheckCircle, AlertCircle, Home, Users, MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const WidgetEmbedPage = () => {
  const { widgetKey } = useParams();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [widget, setWidget] = useState(null);
  const [property, setProperty] = useState(null);
  const [availability, setAvailability] = useState({ blocked_dates: [], booked_ranges: [] });
  
  // Booking flow state
  const [step, setStep] = useState('calendar'); // calendar, details, payment, confirmation
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [pricing, setPricing] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  
  // Guest form state
  const [guestForm, setGuestForm] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    num_guests: 1,
    special_requests: '',
    create_account: false,
    password: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  // Load widget config
  useEffect(() => {
    const loadWidget = async () => {
      try {
        const resp = await fetch(`${BACKEND_URL}/api/embed/${widgetKey}/config`);
        if (!resp.ok) throw new Error('Widget not found');
        const data = await resp.json();
        setWidget(data);
        setProperty(data.property);
        
        // Load availability
        const availResp = await fetch(`${BACKEND_URL}/api/embed/${widgetKey}/availability`);
        if (availResp.ok) {
          setAvailability(await availResp.json());
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadWidget();
  }, [widgetKey]);

  // Calculate pricing when dates change
  useEffect(() => {
    if (checkIn && checkOut) {
      setLoadingPrice(true);
      fetch(`${BACKEND_URL}/api/embed/${widgetKey}/pricing?check_in=${checkIn}&check_out=${checkOut}`)
        .then(r => r.json())
        .then(data => setPricing(data))
        .catch(() => setPricing(null))
        .finally(() => setLoadingPrice(false));
    } else {
      setPricing(null);
    }
  }, [checkIn, checkOut, widgetKey]);

  // Calendar helpers
  const daysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  
  const isDateBlocked = (dateStr) => {
    if (availability.blocked_dates.includes(dateStr)) return true;
    for (const range of availability.booked_ranges) {
      if (dateStr >= range.check_in && dateStr < range.check_out) return true;
    }
    return false;
  };

  const isDatePast = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr < today;
  };

  const formatDateStr = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const handleDateClick = (dateStr) => {
    if (isDateBlocked(dateStr) || isDatePast(dateStr)) return;
    
    if (!checkIn || (checkIn && checkOut)) {
      setCheckIn(dateStr);
      setCheckOut(null);
    } else if (dateStr > checkIn) {
      // Check if any dates in range are blocked
      const start = new Date(checkIn);
      const end = new Date(dateStr);
      let hasBlockedInRange = false;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const ds = d.toISOString().split('T')[0];
        if (isDateBlocked(ds)) {
          hasBlockedInRange = true;
          break;
        }
      }
      if (hasBlockedInRange) {
        setCheckIn(dateStr);
        setCheckOut(null);
      } else {
        setCheckOut(dateStr);
      }
    } else {
      setCheckIn(dateStr);
      setCheckOut(null);
    }
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const days = daysInMonth(currentMonth);
    const startDay = firstDayOfMonth(currentMonth);
    const cells = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startDay; i++) {
      cells.push(<div key={`empty-${i}`} className="p-2" />);
    }
    
    // Day cells
    for (let day = 1; day <= days; day++) {
      const dateStr = formatDateStr(year, month, day);
      const isBlocked = isDateBlocked(dateStr);
      const isPast = isDatePast(dateStr);
      const isCheckIn = dateStr === checkIn;
      const isCheckOut = dateStr === checkOut;
      const isInRange = checkIn && checkOut && dateStr > checkIn && dateStr < checkOut;
      
      let cellStyle = 'p-2 text-center cursor-pointer rounded-lg transition-all text-sm ';
      if (isPast || isBlocked) {
        cellStyle += 'text-gray-300 cursor-not-allowed line-through ';
      } else if (isCheckIn || isCheckOut) {
        cellStyle += `text-white font-bold `;
      } else if (isInRange) {
        cellStyle += 'bg-opacity-30 ';
      } else {
        cellStyle += 'hover:bg-gray-100 ';
      }
      
      cells.push(
        <div
          key={day}
          onClick={() => handleDateClick(dateStr)}
          className={cellStyle}
          style={{
            backgroundColor: isCheckIn || isCheckOut 
              ? widget?.config?.primary_color || '#FF385C'
              : isInRange 
                ? (widget?.config?.primary_color || '#FF385C') + '30'
                : undefined
          }}
        >
          {day}
        </div>
      );
    }
    
    return cells;
  };

  const handleSubmitBooking = async () => {
    if (!checkIn || !checkOut) return;
    
    setSubmitting(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/embed/${widgetKey}/booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...guestForm,
          check_in: checkIn,
          check_out: checkOut
        })
      });
      
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Booking failed');
      
      setBookingResult(data);
      setStep('confirmation');
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const config = widget?.config || {};
  const settings = widget?.settings || {};
  const primaryColor = config.primary_color || '#FF385C';
  const theme = config.theme || 'light';
  const isDark = theme === 'dark';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-white' : 'text-gray-400'}`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className={isDark ? 'text-white' : 'text-gray-600'}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
        color: isDark ? '#ffffff' : '#222222',
        fontFamily: config.font_family === 'system' ? '-apple-system, BlinkMacSystemFont, sans-serif' : config.font_family
      }}
    >
      <div className="max-w-lg mx-auto p-4">
        {/* Property Header */}
        {config.show_property_name && property && (
          <div className="mb-4 pb-4 border-b" style={{ borderColor: isDark ? '#333' : '#eee' }}>
            {config.show_property_image && property.photos?.[0] && (
              <img 
                src={property.photos[0]} 
                alt={property.name}
                className="w-full h-40 object-cover rounded-xl mb-3"
              />
            )}
            <h1 className="text-xl font-bold">{property.name}</h1>
            {property.city && (
              <p className="text-sm opacity-70 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {property.city}, {property.country}
              </p>
            )}
          </div>
        )}

        {/* Calendar Step */}
        {step === 'calendar' && (
          <div>
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <span className="font-semibold">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 mb-4">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                <div key={d} className="p-2 text-center text-xs font-semibold opacity-50">{d}</div>
              ))}
              {renderCalendar()}
            </div>

            {/* Selected Dates & Pricing */}
            {checkIn && (
              <div 
                className="p-4 rounded-xl mb-4"
                style={{ backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5' }}
              >
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <p className="text-xs opacity-50">Check-in</p>
                    <p className="font-semibold">{new Date(checkIn + 'T00:00:00').toLocaleDateString()}</p>
                  </div>
                  <div className="text-center text-2xl opacity-30">→</div>
                  <div className="text-right">
                    <p className="text-xs opacity-50">Check-out</p>
                    <p className="font-semibold">
                      {checkOut ? new Date(checkOut + 'T00:00:00').toLocaleDateString() : 'Select'}
                    </p>
                  </div>
                </div>
                
                {pricing && (
                  <div className="border-t pt-3 mt-3" style={{ borderColor: isDark ? '#444' : '#ddd' }}>
                    <div className="flex justify-between text-sm">
                      <span>{pricing.nights} night{pricing.nights > 1 ? 's' : ''} × {pricing.currency} {pricing.base_price_per_night}</span>
                      <span>{pricing.currency} {pricing.subtotal}</span>
                    </div>
                    {pricing.cleaning_fee > 0 && (
                      <div className="flex justify-between text-sm opacity-70">
                        <span>Cleaning fee</span>
                        <span>{pricing.currency} {pricing.cleaning_fee}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t" style={{ borderColor: isDark ? '#444' : '#ddd' }}>
                      <span>Total</span>
                      <span style={{ color: primaryColor }}>{pricing.currency} {pricing.total}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Continue Button */}
            <Button
              className="w-full"
              disabled={!checkIn || !checkOut}
              onClick={() => setStep('details')}
              style={{ 
                backgroundColor: primaryColor,
                borderRadius: config.border_radius || '12px'
              }}
            >
              {config.button_text || 'Continue'}
            </Button>
          </div>
        )}

        {/* Guest Details Step */}
        {step === 'details' && (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setStep('calendar')} className="mb-2">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Calendar
            </Button>
            
            <h2 className="text-lg font-bold">Guest Details</h2>
            
            <div>
              <Label className="text-sm">Full Name *</Label>
              <Input
                value={guestForm.guest_name}
                onChange={(e) => setGuestForm({ ...guestForm, guest_name: e.target.value })}
                placeholder="Your full name"
                className="mt-1"
                style={{ 
                  backgroundColor: isDark ? '#2a2a2a' : '#fff',
                  borderColor: isDark ? '#444' : '#ddd',
                  color: isDark ? '#fff' : '#222'
                }}
              />
            </div>
            
            <div>
              <Label className="text-sm">Email Address *</Label>
              <Input
                type="email"
                value={guestForm.guest_email}
                onChange={(e) => setGuestForm({ ...guestForm, guest_email: e.target.value })}
                placeholder="email@example.com"
                className="mt-1"
                style={{ 
                  backgroundColor: isDark ? '#2a2a2a' : '#fff',
                  borderColor: isDark ? '#444' : '#ddd',
                  color: isDark ? '#fff' : '#222'
                }}
              />
            </div>
            
            <div>
              <Label className="text-sm">Phone Number</Label>
              <Input
                type="tel"
                value={guestForm.guest_phone}
                onChange={(e) => setGuestForm({ ...guestForm, guest_phone: e.target.value })}
                placeholder="+1 234 567 8900"
                className="mt-1"
                style={{ 
                  backgroundColor: isDark ? '#2a2a2a' : '#fff',
                  borderColor: isDark ? '#444' : '#ddd',
                  color: isDark ? '#fff' : '#222'
                }}
              />
            </div>
            
            <div>
              <Label className="text-sm">Number of Guests</Label>
              <Input
                type="number"
                min="1"
                max={property?.max_guests || 10}
                value={guestForm.num_guests}
                onChange={(e) => setGuestForm({ ...guestForm, num_guests: parseInt(e.target.value) || 1 })}
                className="mt-1"
                style={{ 
                  backgroundColor: isDark ? '#2a2a2a' : '#fff',
                  borderColor: isDark ? '#444' : '#ddd',
                  color: isDark ? '#fff' : '#222'
                }}
              />
            </div>
            
            <div>
              <Label className="text-sm">Special Requests (Optional)</Label>
              <textarea
                value={guestForm.special_requests}
                onChange={(e) => setGuestForm({ ...guestForm, special_requests: e.target.value })}
                placeholder="Any special requests or notes..."
                rows={3}
                className="mt-1 w-full px-3 py-2 border rounded-md text-sm"
                style={{ 
                  backgroundColor: isDark ? '#2a2a2a' : '#fff',
                  borderColor: isDark ? '#444' : '#ddd',
                  color: isDark ? '#fff' : '#222'
                }}
              />
            </div>

            {/* Guest Account Option */}
            {settings.guest_account_required !== 'required' && (
              <div 
                className="p-4 rounded-xl"
                style={{ backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5' }}
              >
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={guestForm.create_account}
                    onChange={(e) => setGuestForm({ ...guestForm, create_account: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-medium text-sm">Create an account</p>
                    <p className="text-xs opacity-70">Save your details for future bookings</p>
                  </div>
                </label>
                
                {guestForm.create_account && (
                  <div className="mt-3">
                    <Label className="text-sm">Password</Label>
                    <Input
                      type="password"
                      value={guestForm.password}
                      onChange={(e) => setGuestForm({ ...guestForm, password: e.target.value })}
                      placeholder="Create a password"
                      className="mt-1"
                      style={{ 
                        backgroundColor: isDark ? '#333' : '#fff',
                        borderColor: isDark ? '#555' : '#ddd',
                        color: isDark ? '#fff' : '#222'
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Booking Summary */}
            {pricing && (
              <div 
                className="p-4 rounded-xl"
                style={{ backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5' }}
              >
                <h3 className="font-semibold mb-2">Booking Summary</h3>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span>Check-in</span>
                    <span>{new Date(checkIn + 'T00:00:00').toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Check-out</span>
                    <span>{new Date(checkOut + 'T00:00:00').toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t" style={{ borderColor: isDark ? '#444' : '#ddd' }}>
                    <span>Total</span>
                    <span style={{ color: primaryColor }}>{pricing.currency} {pricing.total}</span>
                  </div>
                </div>
              </div>
            )}

            <Button
              className="w-full"
              disabled={!guestForm.guest_name || !guestForm.guest_email || submitting}
              onClick={handleSubmitBooking}
              style={{ 
                backgroundColor: primaryColor,
                borderRadius: config.border_radius || '12px'
              }}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              {settings.payment_required === 'required' ? 'Pay & Confirm' : 'Confirm Booking'}
            </Button>
          </div>
        )}

        {/* Confirmation Step */}
        {step === 'confirmation' && bookingResult && (
          <div className="text-center py-8">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: primaryColor + '20' }}
            >
              <CheckCircle className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
            <h2 className="text-xl font-bold mb-2">Booking Confirmed!</h2>
            <p className="opacity-70 mb-6">{bookingResult.message}</p>
            
            <div 
              className="p-4 rounded-xl text-left mb-6"
              style={{ backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5' }}
            >
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="opacity-50">Booking ID</p>
                  <p className="font-mono">{bookingResult.booking_id}</p>
                </div>
                <div>
                  <p className="opacity-50">Status</p>
                  <p className="capitalize">{bookingResult.status}</p>
                </div>
                <div>
                  <p className="opacity-50">Total</p>
                  <p className="font-bold" style={{ color: primaryColor }}>
                    {bookingResult.currency} {bookingResult.total_price}
                  </p>
                </div>
                <div>
                  <p className="opacity-50">Nights</p>
                  <p>{bookingResult.nights}</p>
                </div>
              </div>
            </div>

            {bookingResult.payment_required && bookingResult.status === 'provisional' && (
              <a
                href={`${BACKEND_URL}${bookingResult.guest_portal_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button style={{ backgroundColor: primaryColor, borderRadius: config.border_radius }}>
                  <CreditCard className="w-4 h-4 mr-2" /> Complete Payment
                </Button>
              </a>
            )}
          </div>
        )}

        {/* Powered By Badge */}
        {config.powered_by_ihostpms && (
          <div className="text-center py-4 text-xs opacity-50">
            Powered by <a href="https://ihostpms.com" target="_blank" rel="noopener noreferrer" className="underline">iHostPMS</a>
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetEmbedPage;
